import {
  AmountListItem,
  Button,
  Chip,
  ErrorAlert,
  Icon,
  InfoAlert,
  Loading,
  LoadingAlert,
  Section,
  Table,
  Tooltip,
  UnderlyingYieldBearingAssetListItem,
  WalletTokenListItem,
} from '@/components';
import { TableRowProps } from '@/components/molecules/Table/interfaces/Table';
import { useNavigation } from '@/components/providers';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import { useYieldBearingAssets } from '@/components/providers/postauth/YieldBearingAssetsProvider';
import {
  ErrorCode,
  ErrorInterface,
  YieldBearingAssetInterface,
} from '@/interfaces';
import { replaceTokensInString } from '@/utils/functionalUtils';
import { logError, logInfo } from '@/utils/logger';
import {
  YIELD_BEARING_ASSET,
  YIELD_BEARING_ASSET_ACTIONS_CARD,
} from '@/utils/strings';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { YieldBearingAssetDepositFormDataInterface } from './interfaces/YieldBearingAssetActionsInterfaces';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const spacingConfig = {
  rootGap: 'gap-sm',
  gapBetweenTableCellAndTooltip: 'gap-sm',
  gapBetweenCancelAndConfirmButtons: 'gap-sm',
};
interface Props {
  yieldBearingAsset: YieldBearingAssetInterface | null;
  depositData: YieldBearingAssetDepositFormDataInterface;
}
const ERROR_MESSAGES = {
  [ErrorCode.INSUFFICIENT_BALANCE]: {
    title:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES.FEE_ESTIMATE_FETCH_INSUFFICIENT_BALANCE_ERROR_TITLE,
    description:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES
        .FEE_ESTIMATE_FETCH_INSUFFICIENT_BALANCE_ERROR_DESCRIPTION,
  },
  [ErrorCode.UNSUPPORTED_TOKEN]: {
    title:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES.FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_TITLE,
    description:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES
        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_DESCRIPTION,
  },
  [ErrorCode.SWAP_ROUTE_NOT_FOUND]: {
    title:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES.FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_TITLE,
    description:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES
        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_DESCRIPTION,
  },
  [ErrorCode.INSUFFICIENT_AMOUNT]: {
    title:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES.FEE_ESTIMATE_FETCH_INSUFFICIENT_AMOUNT_ERROR_TITLE,
    description:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES
        .FEE_ESTIMATE_FETCH_INSUFFICIENT_AMOUNT_ERROR_DESCRIPTION,
  },
  [ErrorCode.SERVER_ERROR]: {
    title:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES.FEE_ESTIMATE_FETCH_GENERIC_ERROR_TITLE,
    description:
      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
        .ERROR_MESSAGES.FEE_ESTIMATE_FETCH_GENERIC_ERROR_DESCRIPTION,
  },
} as const;
const YieldBearingAssetInvestmentDepositConfirmationRequest: FC<Props> = ({
  yieldBearingAsset,
  depositData,
}) => {
  const { navigateForward, navigateBack, disableBackNavigation } =
    useNavigation();
  const { tokens: nativeTokens, refreshWalletsAndTransactionsWithBackoff } =
    useWalletsContext();
  const { getDepositFee, deposit } = useYieldBearingAssets();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showFeeEstimationLoadingAlert, setShowFeeEstimationLoadingAlert] =
    useState(false);
  const [showDepositLoadingAlert, setShowDepositLoadingAlert] = useState(false);
  const [feeData, setFeeData] = useState<{
    convenienceFee: string;
    convenienceFeeInUsd: string;
    gasCost: string;
    gasCostInUsd: string;
    bridgeFee: string;
    bridgeFeeInUsd: string;
    totalFee: string;
    totalFeeInUsd: string;
  } | null>(null);
  const [feeFetchError, setFeeFetchError] = useState<ErrorInterface | null>(
    null,
  );
  const [depositError, setDepositError] = useState<ErrorInterface | null>(null);
  const [isFetchingFees, setIsFetchingFees] = useState(false);
  const [snapshotData, setSnapshotData] = useState(() => ({
    token:
      nativeTokens.find((t) => t.id === depositData.token?.id) || undefined,
    underlyingYieldBearingAssets:
      depositData.underlyingYieldBearingAssetsAllocation?.length > 0
        ? depositData.underlyingYieldBearingAssetsAllocation
        : yieldBearingAsset?.underlyingYieldBearingAssets || [],
    amount: depositData.amount,
    feeData: null as typeof feeData,
  }));
  const token = useMemo(
    () => nativeTokens.find((t) => t.id === depositData.token?.id),
    [depositData.token?.id, nativeTokens],
  );
  const underlyingYieldBearingAssets = useMemo(
    () =>
      depositData.underlyingYieldBearingAssetsAllocation?.length > 0
        ? depositData.underlyingYieldBearingAssetsAllocation
        : yieldBearingAsset?.underlyingYieldBearingAssets || [],
    [depositData.underlyingYieldBearingAssetsAllocation, yieldBearingAsset],
  );
  const replacements = useMemo(
    () => ({
      yield_bearing_asset_name: yieldBearingAsset?.name || '',
      yield_bearing_asset_ticker: yieldBearingAsset?.ticker || '',
      sell_asset_name: snapshotData.token?.name || '',
      sell_asset_symbol: snapshotData.token?.symbol || '',
      sell_asset_network:
        `Network ` + snapshotData.token?.blockchainDisplayName || '',
      convenience_fee_percent:
        snapshotData.amount > 0
          ? (
              (parseFloat(snapshotData.feeData?.convenienceFee || '0') /
                snapshotData.amount) *
              100
            ).toFixed(2)
          : '0.00',
    }),
    [yieldBearingAsset, snapshotData],
  );
  const { convenienceFee, convenienceFeeInUsd, networkFee, networkFeeInUsd } =
    useMemo(
      () => ({
        convenienceFee: snapshotData.feeData
          ? parseFloat(snapshotData.feeData.convenienceFee)
          : 0,
        convenienceFeeInUsd: snapshotData.feeData
          ? parseFloat(snapshotData.feeData.convenienceFeeInUsd)
          : 0,
        networkFee: snapshotData.feeData
          ? parseFloat(snapshotData.feeData.gasCost) +
            parseFloat(snapshotData.feeData.bridgeFee || '0')
          : 0,
        networkFeeInUsd: snapshotData.feeData
          ? parseFloat(snapshotData.feeData.gasCostInUsd) +
            parseFloat(snapshotData.feeData.bridgeFeeInUsd || '0')
          : 0,
      }),
      [snapshotData.feeData],
    );
  useEffect(() => {
    let isMounted = true;
    let feeEstimationTimer: NodeJS.Timeout | null = null;
    const fetchFees = async () => {
      if (yieldBearingAsset && snapshotData.token && snapshotData.amount > 0) {
        setIsFetchingFees(true);
        feeEstimationTimer = setTimeout(() => {
          if (isMounted) {
            setShowFeeEstimationLoadingAlert(true);
          }
        }, 5000);
        try {
          const result = await getDepositFee(
            yieldBearingAsset.ticker,
            snapshotData.underlyingYieldBearingAssets,
            {
              symbol: snapshotData.token.symbol,
              address: snapshotData.token.address,
              blockchain: snapshotData.token.blockchain,
            },
            snapshotData.amount.toString(),
          );
          if (isMounted && snapshotData.feeData === null) {
            setFeeData(result);
            setSnapshotData((prev) => ({ ...prev, feeData: result }));
          }
        } catch (error: any) {
          if (isMounted) {
            setFeeFetchError(error);
            logError(error, 'DepositConfirmation.fetchFees');
          }
        } finally {
          if (isMounted) {
            if (feeEstimationTimer) clearTimeout(feeEstimationTimer);
            setIsFetchingFees(false);
            setShowFeeEstimationLoadingAlert(false);
          }
        }
      }
    };
    fetchFees();
    return () => {
      isMounted = false;
      if (feeEstimationTimer) clearTimeout(feeEstimationTimer);
    };
  }, []);
  const handleConfirm = useCallback(async () => {
    if (!yieldBearingAsset || !token || depositData.amount <= 0) {
      logError('Invalid deposit data', 'DepositConfirmation.handleConfirm', {
        yieldBearingAsset,
        token,
        depositData,
      });
      return;
    }
    setIsProcessing(true);
    setDepositError(null);
    disableBackNavigation();
    const depositTimer = setTimeout(() => {
      setShowDepositLoadingAlert(true);
    }, 5000);
    try {
      const response = await deposit(
        yieldBearingAsset.ticker,
        underlyingYieldBearingAssets.map((asset) => ({
          id: asset.id,
          composition: asset.composition,
        })),
        {
          symbol: token.symbol,
          address: token.address,
          blockchain: token.blockchain,
        },
        depositData.amount.toString(),
      );
      logInfo(
        'Deposit successful',
        'DepositConfirmation.handleConfirm',
        response,
      );
      depositData.response = response;
      refreshWalletsAndTransactionsWithBackoff();
      navigateForward('deposit-final');
    } catch (error: any) {
      const errorDetails: ErrorInterface = error;
      logError(errorDetails, 'DepositConfirmation.handleConfirm', {
        yieldBearingAsset,
        token,
        depositData,
      });
      setDepositError(errorDetails);
    } finally {
      clearTimeout(depositTimer);
      setIsProcessing(false);
      setShowDepositLoadingAlert(false);
    }
  }, [
    yieldBearingAsset,
    token,
    depositData,
    deposit,
    underlyingYieldBearingAssets,
    disableBackNavigation,
    navigateForward,
    refreshWalletsAndTransactionsWithBackoff,
  ]);
  const InfoTooltip: FC<{ titleKey: string; descriptionKey: string }> =
    useCallback(
      ({ titleKey, descriptionKey }) => (
        <Tooltip
          trigger={<Icon name="question" intent="accent" />}
          align="right"
          isFullBleed
        >
          <InfoAlert
            title={replaceTokensInString(titleKey, replacements)}
            description={replaceTokensInString(descriptionKey, replacements)}
            intent="transparent"
          />
        </Tooltip>
      ),
      [replacements],
    );
  const AmountDisplay: FC<{
    amount: number;
    subAmount: number;
    symbol: string;
  }> = useCallback(
    ({ amount, subAmount, symbol }) => (
      <AmountListItem amount={amount} subAmount={subAmount} symbol={symbol} />
    ),
    [],
  );
  const rows = useMemo<TableRowProps[]>(
    () => [
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.BOUGHT_ASSET.LABEL,
          },
          {
            mainText: (
              <div className="grid gap-sm-wholestep-dec">
                {snapshotData.underlyingYieldBearingAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className={`flex items-center ${spacingConfig.gapBetweenTableCellAndTooltip} ${borderClassToVisualizeElementsAndSpacing}`}
                  >
                    <UnderlyingYieldBearingAssetListItem
                      underlyingYieldBearingAsset={asset}
                      subText={`${asset.symbol} (${(
                        asset.composition * 100
                      ).toFixed(2)}%)`}
                      align="right"
                    />
                    <InfoTooltip
                      titleKey={
                        YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                          .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.BOUGHT_ASSET
                          .TOOLTIP_TITLE
                      }
                      descriptionKey={
                        YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                          .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.BOUGHT_ASSET
                          .TOOLTIP_DESCRIPTION
                      }
                    />
                  </div>
                ))}
              </div>
            ),
            align: 'right',
          },
        ],
      },
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.SOLD_ASSET.LABEL,
          },
          {
            mainText: (
              <div
                className={`flex items-center ${spacingConfig.gapBetweenTableCellAndTooltip}`}
              >
                <WalletTokenListItem
                  token={snapshotData.token}
                  align="right"
                  showTicker
                  showBalance={false}
                  showWalletAddress={false}
                  showNetwork
                  showNetworkPrefix
                />
                <InfoTooltip
                  titleKey={
                    YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                      .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.SOLD_ASSET
                      .TOOLTIP_TITLE
                  }
                  descriptionKey={
                    YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                      .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.SOLD_ASSET
                      .TOOLTIP_DESCRIPTION
                  }
                />
              </div>
            ),
            align: 'right',
          },
        ],
      },
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.AMOUNT.LABEL,
          },
          {
            mainText: (
              <div
                className={`flex items-center ${spacingConfig.gapBetweenTableCellAndTooltip}`}
              >
                <AmountDisplay
                  amount={snapshotData.amount}
                  subAmount={
                    snapshotData.amount * (snapshotData.token?.priceInUsd || 0)
                  }
                  symbol={snapshotData.token?.symbol || ''}
                />
                <InfoTooltip
                  titleKey={
                    YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                      .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.AMOUNT.TOOLTIP_TITLE
                  }
                  descriptionKey={
                    YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                      .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.AMOUNT
                      .TOOLTIP_DESCRIPTION
                  }
                />
              </div>
            ),
            align: 'right',
          },
        ],
      },
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.NETWORK_FEE.LABEL,
          },
          {
            mainText: (
              <div
                className={`flex items-center ${spacingConfig.gapBetweenTableCellAndTooltip}`}
              >
                {isFetchingFees ? (
                  <Loading size="xs" />
                ) : snapshotData.feeData ? (
                  <AmountDisplay
                    amount={networkFee}
                    subAmount={networkFeeInUsd}
                    symbol={snapshotData.token?.symbol || ''}
                  />
                ) : (
                  <Chip
                    text={
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_REQUEST.ERROR_MESSAGES
                        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_NETWORK_FEE_PLACEHOLDER
                    }
                    intent="error"
                    setChipToNoMargin={true}
                  />
                )}
                <InfoTooltip
                  titleKey={
                    YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                      .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.NETWORK_FEE
                      .TOOLTIP_TITLE
                  }
                  descriptionKey={
                    YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                      .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.NETWORK_FEE
                      .TOOLTIP_DESCRIPTION
                  }
                />
              </div>
            ),
            align: 'right',
          },
        ],
      },
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.CONVENIENCE_FEE.LABEL,
          },
          {
            mainText: (
              <div
                className={`flex items-center ${spacingConfig.gapBetweenTableCellAndTooltip}`}
              >
                {isFetchingFees ? (
                  <Loading size="xs" />
                ) : snapshotData.feeData ? (
                  convenienceFee === 0 ? (
                    <Chip
                      text={
                        YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                          .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.CONVENIENCE_FEE
                          .CHIP_LABEL_FOR_FREE
                      }
                      intent="accent"
                      setChipToNoMargin={true}
                    />
                  ) : (
                    <AmountDisplay
                      amount={convenienceFee}
                      subAmount={convenienceFeeInUsd}
                      symbol={snapshotData.token?.symbol || ''}
                    />
                  )
                ) : (
                  <Chip
                    text={
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_REQUEST.ERROR_MESSAGES
                        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_CONVENIENCE_FEE_PLACEHOLDER
                    }
                    intent="error"
                    setChipToNoMargin={true}
                  />
                )}
                <InfoTooltip
                  titleKey={
                    YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                      .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.CONVENIENCE_FEE
                      .TOOLTIP_TITLE
                  }
                  descriptionKey={
                    YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                      .DEPOSIT_CONFIRMATION_REQUEST.DETAILS.CONVENIENCE_FEE
                      .TOOLTIP_DESCRIPTION
                  }
                />
              </div>
            ),
            align: 'right',
          },
        ],
      },
    ],
    [
      snapshotData,
      isFetchingFees,
      AmountDisplay,
      networkFee,
      networkFeeInUsd,
      convenienceFee,
      convenienceFeeInUsd,
      InfoTooltip,
      spacingConfig.gapBetweenTableCellAndTooltip,
      borderClassToVisualizeElementsAndSpacing,
    ],
  );
  const getErrorContent = useCallback(
    (error: ErrorInterface) => {
      if (
        error.errorCode === ErrorCode.SERVER_ERROR &&
        error.details === 'Invalid tokens'
      ) {
        return {
          title: replaceTokensInString(
            YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
              .DEPOSIT_CONFIRMATION_REQUEST.ERROR_MESSAGES
              .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_TITLE,
            replacements,
          ),
          description: replaceTokensInString(
            YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
              .DEPOSIT_CONFIRMATION_REQUEST.ERROR_MESSAGES
              .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_DESCRIPTION,
            replacements,
          ),
        };
      }
      if (
        error.errorCode &&
        ERROR_MESSAGES[error.errorCode as keyof typeof ERROR_MESSAGES]
      ) {
        const errorConfig =
          ERROR_MESSAGES[error.errorCode as keyof typeof ERROR_MESSAGES];
        const baseError = {
          title: replaceTokensInString(errorConfig.title, replacements),
          description: replaceTokensInString(
            errorConfig.description,
            replacements,
          ),
        };
        if (
          error.errorCode === ErrorCode.INSUFFICIENT_AMOUNT &&
          error.details &&
          error.details.nativeAsset
        ) {
          const { nativeAsset } = error.details;
          const additionalMessage = `In particular, ${nativeAsset.name}${
            nativeAsset.symbol ? ` (${nativeAsset.symbol})` : ''
          } needs a minimum of ${nativeAsset.minimumDeposit}${
            nativeAsset.symbol ? ` ${nativeAsset.symbol}` : ''
          } to be deployed.`;
          return {
            ...baseError,
            description: `${baseError.description} ${additionalMessage}`,
          };
        }
        return baseError;
      }
      return {
        title: replaceTokensInString(
          YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
            .ERROR_MESSAGES.FEE_ESTIMATE_FETCH_GENERIC_ERROR_TITLE,
          replacements,
        ),
        description: replaceTokensInString(
          YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_REQUEST
            .ERROR_MESSAGES
            .FEE_ESTIMATE_FETCH_GENERIC_ERROR_DESCRIPTION,
          replacements,
        ),
      };
    },
    [replacements],
  );
  const renderBody = useCallback(
    () => (
      <Section
        body={
          <div className={`flex flex-col ${spacingConfig.rootGap}`}>
            <Table rows={rows} columnWidths={[50, 50]} />
          </div>
        }
        className={borderClassToVisualizeElementsAndSpacing}
      />
    ),
    [rows, feeFetchError, depositError, getErrorContent],
  );
  const renderFooter = useCallback(
    () => (
      <div
        className={`w-full flex flex-row items-center ${spacingConfig.gapBetweenCancelAndConfirmButtons}`}
      >
        <Button
          onClick={() => navigateBack(`deposit-initial`)}
          fullWidth
          disabled={isProcessing}
        >
          {
            YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
              .DEPOSIT_CONFIRMATION_REQUEST.CANCEL_BUTTON_LABEL
          }
        </Button>
        <Button
          onClick={handleConfirm}
          fullWidth
          loading={isProcessing || isFetchingFees}
          disabled={isProcessing || isFetchingFees || !feeData}
        >
          {
            YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
              .DEPOSIT_CONFIRMATION_REQUEST.CONFIRM_BUTTON_LABEL
          }
        </Button>
      </div>
    ),
    [navigateBack, isProcessing, isFetchingFees, feeData, handleConfirm],
  );
  return (
    <div
      className={`flex flex-col flex-grow w-full h-full ${spacingConfig.rootGap}`}
    >
      {renderBody()}
      {showFeeEstimationLoadingAlert && (
        <LoadingAlert
          title={
            YIELD_BEARING_ASSET.PROCESSING
              .DEPOSIT_FEE_ESTIMATION_TAKING_LONGER_TITLE
          }
          description={
            YIELD_BEARING_ASSET.PROCESSING
              .DEPOSIT_FEE_ESTIMATION_TAKING_LONGER_DESCRIPTION
          }
        />
      )}
      {showDepositLoadingAlert && (
        <LoadingAlert
          title={
            YIELD_BEARING_ASSET.PROCESSING
              .DEPOSIT_IN_PROGRESS_TAKING_LONGER_TITLE
          }
          description={
            YIELD_BEARING_ASSET.PROCESSING
              .DEPOSIT_IN_PROGRESS_TAKING_LONGER_DESCRIPTION
          }
        />
      )}
      {feeFetchError && getErrorContent(feeFetchError) && (
        <ErrorAlert {...getErrorContent(feeFetchError)} />
      )}
      {depositError && getErrorContent(depositError) && (
        <ErrorAlert {...getErrorContent(depositError)} />
      )}
      {renderFooter()}
    </div>
  );
};
export default React.memo(
  YieldBearingAssetInvestmentDepositConfirmationRequest,
);
